import type { EnterpriseUserAdminView_UserIdentifier } from '__generated__/graphql-types-do-not-use';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import { enterpriseContractUtilizationType } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import S3Upload from 'bundles/enterprise-admin-program-users/utils/s3uploader';
import type { AttributeSelections } from 'bundles/enterprise-admin-users/types';
import type { InjectedNaptime } from 'bundles/naptimejs';
import EnterpriseLearnerProfileImportOperations from 'bundles/naptimejs/resources/enterpriseLearnerProfileImportOperations.v1';

import _t from 'i18n!nls/enterprise-admin-users';

const LOCAL_STORAGE_KEY = 'membersListSelectedColumnsKey';
const LOCAL_STORAGE_PROGRAM_SCOPE_KEY = 'membersListProgramScopeSelectedColumnsKey';

const { ENROLLMENT_CAP, USER_LICENSE, NO_LIMIT, USER_LICENSE_WITH_ENROLLMENT_CAP } = enterpriseContractUtilizationType;

export const getFormattedContractType = (limitType?: string | null) => {
  switch (limitType) {
    case USER_LICENSE:
      return _t('Licenses');
    case ENROLLMENT_CAP:
      return _t('Enrollments');
    case USER_LICENSE_WITH_ENROLLMENT_CAP:
      return _t('Licenses with enrollments');
    case NO_LIMIT:
      return _t('Unlimited');
    default:
      return _t('Unknown');
  }
};

export const renderUSDate = (timestamp?: number) => {
  return timestamp ? formatDateTimeDisplay(timestamp, MOMENT_FORMATS.US_LOCAL_DATE) : '';
};

const getLocalStorageKey = (isProgramScope: boolean) => {
  if (isProgramScope) {
    return LOCAL_STORAGE_PROGRAM_SCOPE_KEY;
  } else {
    return LOCAL_STORAGE_KEY;
  }
};

export const getSelectedColumnsFromLocalStorage = (isProgramScope: boolean): AttributeSelections[] =>
  localStorageEx.getItem(getLocalStorageKey(isProgramScope), JSON.parse, undefined);

export const storeSelectedColumnsToLocalStorage = (selectedColumns: AttributeSelections[], isProgramScope: boolean) => {
  localStorageEx.setItem(getLocalStorageKey(isProgramScope), selectedColumns, JSON.stringify);
};

export const clearSelectedColumnsFromLocalStorage = (isProgramScope: boolean) =>
  localStorageEx.removeItem(getLocalStorageKey(isProgramScope));

/**
 * Enclose all ProgramInvitationApi related logic in this class
 * - Share variables across different api calls, e.g. naptime, program, resourceName
 * - Put apiPromise and apiNotification generation logic together, so it's easy to keep track
 * - Provide a way to consistently deal with apiPromise, both for generic promise and Naptime mutations
 * - Allow any component to share the resource, so long as the apiManager instance is passed down
 */
export class ProgramMembersApiManager {
  declare _executeMutation: InjectedNaptime['executeMutation'];

  declare _thirdPartyOrganizationId: string;

  constructor({
    executeMutation,
    thirdPartyOrganizationId,
  }: {
    executeMutation: InjectedNaptime['executeMutation'];
    thirdPartyOrganizationId: string;
  }) {
    this._executeMutation = executeMutation;
    this._thirdPartyOrganizationId = thirdPartyOrganizationId;
  }

  getGenerateBulkUploadLocationAction() {
    return EnterpriseLearnerProfileImportOperations.action(
      'generateBulkUploadLocation',
      {},
      {
        thirdPartyOrganizationId: this._thirdPartyOrganizationId,
      }
    );
  }

  uploadFile(file: File) {
    const uploader = new S3Upload({
      getSignedUrl: (callback: $TSFixMe) => {
        return this._executeMutation(this.getGenerateBulkUploadLocationAction()).then((response) => {
          return callback(response.data);
        });
      },
      onFinishS3Put(signResult) {
        return signResult;
      },
    });
    return uploader.handleFileSelect(file);
  }

  getPreviewImportPromise({ key, bucket }: { key: string; bucket: string }) {
    return this._executeMutation(
      EnterpriseLearnerProfileImportOperations.action(
        'previewCSVImport',
        {
          thirdPartyOrganizationId: this._thirdPartyOrganizationId,
          file: {
            key,
            bucket,
          },
        },
        {}
      )
    );
  }

  getProcessImportPromise({ key, bucket }: { key: string; bucket: string }) {
    return this._executeMutation(
      EnterpriseLearnerProfileImportOperations.action(
        'processCSVImport',
        {
          thirdPartyOrganizationId: this._thirdPartyOrganizationId,
          file: {
            key,
            bucket,
          },
        },
        {}
      )
    );
  }
}

export const isAnonymizedUser = ({ name, email }: { name: string; email: string }) => {
  return name.toUpperCase() === 'ANONYMIZED NAME' && email.toUpperCase() === 'ANONYMIZED EMAIL';
};

export const getUserIdentifier = (
  userIdentifier: EnterpriseUserAdminView_UserIdentifier,
  withPrefix: boolean
): string => {
  if (userIdentifier.__typename === 'EnterpriseUserAdminView_Email') {
    return withPrefix ? `email~${userIdentifier.email}` : userIdentifier.email;
  } else if (userIdentifier.__typename === 'EnterpriseUserAdminView_UserId') {
    return withPrefix ? `userId~${userIdentifier.userId}` : userIdentifier.userId;
  } else {
    return '';
  }
};
